import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Wrench,
  Bot,
  Calendar,
  Rocket,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CATEGORIES = [
  {
    id: "general",
    label: "general",
    icon: Sparkles,
    items: [
      {
        q: "What does Kyvixe actually do?",
        a: "We build websites, AI chatbots, and automations that handle customer conversations for you — so restaurants, clinics, salons, and other businesses never miss a lead again.",
      },
      {
        q: "How is Kyvixe different from a regular web agency?",
        a: "We don't just build a website and walk away — we connect it to AI chatbots, booking systems, and automations so it actively works to convert visitors into customers 24/7.",
      },
      {
        q: "How long does implementation take?",
        a: "Most projects go live within 2–4 weeks, depending on scope — simple chatbot or booking setups can be faster.",
      },
    ],
  },
  {
    id: "services",
    label: "services & pricing",
    icon: Wrench,
    items: [
      {
        q: "How much does it cost to work with Kyvixe?",
        a: "Every business is different, so we provide a custom quote after a quick discovery call — book one and we'll map out exactly what you need.",
      },
      {
        q: "Can Kyvixe integrate with tools I already use?",
        a: "Yes — we connect with most existing CRMs, booking tools, and payment systems, so you don't have to rebuild your workflow from scratch.",
      },
    ],
  },
  {
    id: "ai",
    label: "ai chatbots & automations",
    icon: Bot,
    items: [
      {
        q: "Will the AI chatbot sound robotic with my customers?",
        a: "No — we train it on your business so it responds like a real team member, whether it's answering a salon's pricing questions or booking a table for a restaurant.",
      },
      {
        q: "Can automations handle WhatsApp, Instagram, and Facebook messages?",
        a: "Yes — one system handles all your channels, so a clinic or gym never has to juggle multiple inboxes to respond to patients or members.",
      },
    ],
  },
  {
    id: "bookings",
    label: "bookings",
    icon: Calendar,
    items: [
      {
        q: "Can customers book appointments directly through the chatbot?",
        a: "Absolutely — whether it's a spa booking a facial or a clinic scheduling a patient visit, the AI handles the entire reservation without any manual back-and-forth.",
      },
    ],
  },
  {
    id: "getting-started",
    label: "getting started",
    icon: Rocket,
    items: [
      {
        q: "What industries do you work with?",
        a: "We work with restaurants, clinics, salons & spas, real estate, hotels, gyms, e-commerce, schools, and law firms — if your customers message you, we can help.",
      },
      {
        q: "How do I get started?",
        a: "Book a free discovery call — we'll learn about your business and show you exactly how automation can start saving you leads within weeks.",
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="bg-section-glow" data-testid="faq-page">
      {/* Hero */}
      <section className="pt-20 pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h1
            className="font-display lowercase font-bold text-5xl sm:text-6xl tracking-tighter"
            data-testid="faq-heading"
          >
            answers, without <span className="text-blue-500">the sales pitch.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-2xl mx-auto">
            everything you'd want to know before booking a call — pricing,
            timelines, tools, and how the ai actually sounds.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-8">
          {CATEGORIES.map((cat, ci) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: ci * 0.05 }}
              className="kyv-card p-6 sm:p-8"
              data-testid={`faq-category-${cat.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-full bg-blue-500/10 text-blue-400">
                  <cat.icon size={18} />
                </div>
                <h2 className="font-display lowercase font-bold text-2xl tracking-tight">
                  {cat.label}
                </h2>
              </div>

              <Accordion
                type="single"
                collapsible
                className="w-full"
                data-testid={`faq-accordion-${cat.id}`}
              >
                {cat.items.map((it, idx) => {
                  const value = `${cat.id}-${idx}`;
                  return (
                    <AccordionItem
                      key={value}
                      value={value}
                      className="border-white/10"
                    >
                      <AccordionTrigger
                        className="text-left text-white hover:no-underline hover:text-blue-300 font-medium text-base sm:text-lg py-4"
                        data-testid={`faq-question-${cat.id}-${idx}`}
                      >
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent
                        className="text-gray-300 text-sm sm:text-base leading-relaxed pb-4"
                        data-testid={`faq-answer-${cat.id}-${idx}`}
                      >
                        {it.a}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div
            className="relative kyv-card p-10 md:p-14 text-center overflow-hidden"
            data-testid="faq-cta"
          >
            <div className="absolute inset-0 bg-hero-glow opacity-70" />
            <div className="relative">
              <h2 className="font-display lowercase font-bold text-3xl sm:text-4xl tracking-tighter">
                still have <span className="text-blue-500">questions?</span>
              </h2>
              <p className="mt-3 text-gray-300">
                book a 30-minute discovery call — we'll answer everything and
                map out your fastest win.
              </p>
              <div className="mt-6">
                <Link
                  to="/contact#book"
                  className="kyv-btn-primary inline-flex items-center gap-2"
                  data-testid="faq-book-a-call-btn"
                >
                  book a call <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
